import type { ReactNode } from "react";
import { Topbar } from "./Topbar";
import { Footer } from "./Footer";
import { LogoReferences } from "./LogoReferences";
import { Hero } from "./Hero";
import { Section } from "./Section";
import { RevealOnScroll } from "./RevealOnScroll";
import { RelatedServices } from "./RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";
import { Link } from "@tanstack/react-router";

// Logo is loaded via import.meta.glob so the page also builds when the asset
// has not been added yet. Drop a file at src/assets/logos/gonzalads.png|svg|webp
// and it shows up automatically in hero-comparison header and CTA Card A.
const LOGO_MODULES = import.meta.glob("../../assets/logos/gonzalads.{png,svg,webp,jpg}", {
  eager: true,
  import: "default",
}) as Record<string, string>;
const GONZAL_LOGO: string | undefined = Object.values(LOGO_MODULES)[0];

export type GonzalContent = {
  lang: "de" | "en";
  eyebrow: string;
  headline: ReactNode;
  subline: string;
  metaStrip: string[];
  breadcrumbs: { label: string; to?: string }[];
  standpunktKicker: string;
  standpunktHeadline: ReactNode;
  standpunktParagraphs: ReactNode[];

  pillarsKicker: string;
  pillarsHeadline: ReactNode;
  pillars: { num: string; name: string; desc: string }[];

  uspsKicker: string;
  uspsHeadline: ReactNode;
  usps: { num: string; name: string; detail: string }[];

  compareKicker: string;
  compareHeadline: ReactNode;
  compareLeftTitle: string;
  compareRightTitle: string;
  compareRows: { left: string; right: string }[];

  valueKicker: string;
  valueHeadline: ReactNode;
  values: { strong: string; rest: string }[];

  audienceKicker: string;
  audienceHeadline: ReactNode;
  audience: { strong: string; rest: string }[];

  calvariusKicker: string;
  calvariusHeadline: ReactNode;
  calvariusBody: string;

  ctaKicker: string;
  ctaHeadline: ReactNode;

  cardA: {
    eyebrow: string;
    headline: string;
    body: string;
    button: string;
  };
  cardB: {
    eyebrow: string;
    headline: string;
    body: string;
    button: string;
    to: string;
  };
};

function Wordmark({ size = "md" }: { size?: "sm" | "md" | "lg" }) {
  const cls =
    size === "lg"
      ? "text-5xl md:text-6xl"
      : size === "sm"
      ? "text-2xl"
      : "text-3xl md:text-4xl";
  return (
    <span className="inline-flex items-baseline gap-3">
      <span className={`font-display font-semibold tracking-tight ${cls}`}>GonzalAds</span>
      <span className="font-display italic text-ink-3 text-base md:text-lg">Speedy Ad Management</span>
    </span>
  );
}

function ExternalIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="ml-1 inline-block"
    >
      <path d="M7 17L17 7" />
      <path d="M7 7h10v10" />
    </svg>
  );
}

export function GonzalAdsPage(p: GonzalContent) {
  const gonzalLogoLabel = p.lang === "en" ? "Logo: GonzalAds — Speedy Ad Management" : "Logo: GonzalAds — Schnelles Anzeigen-Management";

  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          number="01"
          eyebrow={p.eyebrow}
          headline={p.headline}
          lead={p.subline}
          metaStrip={p.metaStrip}
          breadcrumbs={p.breadcrumbs}
        />

        {/* Wordmark below hero */}
        <Section number="02" kicker={p.standpunktKicker} headline={p.standpunktHeadline}>
          <RevealOnScroll>
            <div className="mb-10">
              <Wordmark size="lg" />
            </div>
            <div className="prose max-w-2xl space-y-5 text-[17px] leading-relaxed text-ink-2">
              {p.standpunktParagraphs.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        {/* Pillars */}
        <Section variant="tinted" number="03" kicker={p.pillarsKicker} headline={p.pillarsHeadline}>
          <RevealOnScroll>
            <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2 lg:grid-cols-5">
              {p.pillars.map((pi) => (
                <div key={pi.num} className="bg-white p-6">
                  <div className="font-mono text-xs uppercase tracking-wider text-ink-4">{pi.num}</div>
                  <h3 className="mt-3 font-display text-xl font-semibold text-ink">{pi.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{pi.desc}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        {/* USPs */}
        <Section number="04" kicker={p.uspsKicker} headline={p.uspsHeadline}>
          <RevealOnScroll>
            <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2 lg:grid-cols-4">
              {p.usps.map((u) => (
                <div key={u.num} className="bg-white p-6">
                  <div className="font-mono text-xs uppercase tracking-wider text-ink-4">{u.num}</div>
                  <h3 className="mt-3 font-display text-lg font-semibold text-ink leading-snug">{u.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{u.detail}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        {/* Comparison */}
        <Section variant="tinted" number="05" kicker={p.compareKicker} headline={p.compareHeadline}>
          <RevealOnScroll>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Left: Business Manager */}
              <div className="rounded-lg border border-rule bg-white p-6">
                <div className="mb-6 flex items-center gap-3 border-b border-rule pb-4">
                  <span
                    aria-hidden
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-yellow-100 text-yellow-700"
                  >
                    !
                  </span>
                  <h3 className="font-display text-xl font-semibold text-ink">{p.compareLeftTitle}</h3>
                </div>
                <ul className="space-y-4">
                  {p.compareRows.map((r, i) => (
                    <li key={i} className="flex items-start gap-3 text-ink-2">
                      <span aria-hidden className="mt-1 text-yellow-600">▲</span>
                      <span>{r.left}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Right: GonzalAds */}
              <div className="rounded-lg border-2 border-lime/50 bg-white p-6">
                <div className="mb-6 flex items-center gap-3 border-b border-rule pb-4">
                  {GONZAL_LOGO ? (
                    <img src={GONZAL_LOGO} alt={gonzalLogoLabel} title={gonzalLogoLabel} width={160} height={32} loading="lazy" decoding="async" className="h-8 w-auto" />
                  ) : (
                    <span
                      aria-hidden
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-lime/30 text-ink"
                    >
                      ✓
                    </span>
                  )}
                  <h3 className="font-display text-xl font-semibold text-ink">{p.compareRightTitle}</h3>
                </div>
                <ul className="space-y-4">
                  {p.compareRows.map((r, i) => (
                    <li key={i} className="flex items-start gap-3 text-ink-2">
                      <span aria-hidden className="mt-1 text-lime-700">✓</span>
                      <span className="font-medium text-ink">{r.right}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </RevealOnScroll>
        </Section>

        {/* Value */}
        <Section number="06" kicker={p.valueKicker} headline={p.valueHeadline}>
          <RevealOnScroll>
            <ul className="space-y-4">
              {p.values.map((it, i) => (
                <li key={i} className="flex gap-4 rounded-lg border border-rule bg-white p-5">
                  <span className="text-cyan" aria-hidden>●</span>
                  <div className="text-ink-2 leading-relaxed">
                    <strong className="font-semibold text-ink">{it.strong}</strong> {it.rest && <>— {it.rest}</>}
                  </div>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        {/* Audience */}
        <Section variant="tinted" number="07" kicker={p.audienceKicker} headline={p.audienceHeadline}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {p.audience.map((a, i) => (
                <div key={i} className="flex items-baseline gap-4 bg-white px-5 py-4">
                  <span className="text-cyan" aria-hidden>●</span>
                  <div className="flex-1">
                    <div className="font-medium text-ink">{a.strong}</div>
                    <div className="font-mono text-[11px] uppercase tracking-wider text-ink-4">{a.rest}</div>
                  </div>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        {/* Calvarius connection */}
        <Section number="08" kicker={p.calvariusKicker} headline={p.calvariusHeadline}>
          <RevealOnScroll>
            <p className="max-w-2xl text-[17px] leading-relaxed text-ink-2">{p.calvariusBody}</p>
          </RevealOnScroll>
        </Section>

        <LogoReferences locale={p.lang} number="09" />

        <RelatedServices
          locale={p.lang}
          fromId="gonzalads"
          number="10"
          items={relatedBySource["gonzalads"]}
        />

        {/* Dual CTA */}
        <Section variant="deep" number="11" kicker={p.ctaKicker} headline={p.ctaHeadline}>
          <RevealOnScroll>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Card A — Direct trial (primary, with logo) */}
              <div className="rounded-lg border border-lime/30 bg-white p-8 text-ink">
                {GONZAL_LOGO ? (
                  <img src={GONZAL_LOGO} alt={gonzalLogoLabel} title={gonzalLogoLabel} width={240} height={48} loading="lazy" decoding="async" className="mb-6 h-12 w-auto" />
                ) : (
                  <div className="mb-6">
                    <Wordmark size="md" />
                  </div>
                )}
                <p className="font-mono text-xs uppercase tracking-wider text-ink-4">{p.cardA.eyebrow}</p>
                <h3 className="mt-3 font-display text-2xl font-semibold leading-snug">{p.cardA.headline}</h3>
                <p className="mt-4 text-ink-3 leading-relaxed">{p.cardA.body}</p>
                <a
                  href="https://gonzalads.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center gap-2 rounded-md bg-lime px-6 py-3 font-mono text-sm font-medium uppercase tracking-wider text-ink transition-colors hover:bg-lime/90"
                >
                  {p.cardA.button}
                  <ExternalIcon />
                </a>
              </div>

              {/* Card B — Mandate integration (secondary) */}
              <div className="rounded-lg border border-white/15 bg-white/5 p-8 text-white">
                <p className="font-mono text-xs uppercase tracking-wider text-white/85">{p.cardB.eyebrow}</p>
                <h3 className="mt-3 font-display text-2xl font-semibold leading-snug">{p.cardB.headline}</h3>
                <p className="mt-4 leading-relaxed text-white/75">{p.cardB.body}</p>
                <Link
                  to={p.cardB.to}
                  className="mt-6 inline-flex items-center gap-2 rounded-md border border-white/30 px-6 py-3 font-mono text-sm font-medium uppercase tracking-wider text-white transition-colors hover:bg-white/10"
                >
                  {p.cardB.button} →
                </Link>
              </div>
            </div>
          </RevealOnScroll>
        </Section>
      </main>
      <Footer />
    </>
  );
}
